type IdleCallbackHandle = number;
type PostPaintIdleDeadline = {
  didTimeout: boolean;
  timeRemaining: () => number;
};
type PostPaintIdleCallback = (deadline: PostPaintIdleDeadline) => void;
type PostPaintWindow = Window & {
  cancelIdleCallback?: (handle: IdleCallbackHandle) => void;
  requestIdleCallback?: (
    callback: PostPaintIdleCallback,
    options?: { timeout?: number },
  ) => IdleCallbackHandle;
};

/**
 * Defer a callback until after the browser has painted, then run it during an
 * idle slice (or after `fallbackDelayMs` if `requestIdleCallback` is missing).
 *
 * Used for non-critical post-paint work that should not compete with first
 * paint or input handling. Mirrors the
 * `requestAnimationFrame` → `requestIdleCallback(timeout)` → `setTimeout` chain.
 *
 * @param callback the work to run once idle.
 * @param fallbackDelayMs used as the `setTimeout` fallback when
 *   `requestIdleCallback` is unavailable, and as the idle timeout when
 *   `idleTimeoutMs` is not provided.
 * @param idleTimeoutMs optional override for the `requestIdleCallback` timeout.
 *   Pass this when a call site wants a longer idle grace window than its
 *   hard fallback (e.g. route warmup uses 250ms fallback but 2000ms idle grace).
 * @returns a cancel function — call it from a `useEffect` cleanup to abort a
 *   pending callback when its deps change.
 */
export function schedulePostPaint(
  callback: () => void,
  fallbackDelayMs: number,
  idleTimeoutMs: number = fallbackDelayMs,
): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  const postPaintWindow = window as PostPaintWindow;
  let cancelled = false;
  let idleHandle: IdleCallbackHandle | null = null;
  let timeoutHandle: number | null = null;

  const requestFrame =
    typeof window.requestAnimationFrame === "function"
      ? window.requestAnimationFrame.bind(window)
      : (frameCallback: FrameRequestCallback) =>
          window.setTimeout(() => {
            frameCallback(window.performance.now());
          }, 16);
  const cancelFrame =
    typeof window.cancelAnimationFrame === "function"
      ? window.cancelAnimationFrame.bind(window)
      : window.clearTimeout.bind(window);

  const runCallback = () => {
    if (cancelled) {
      return;
    }

    callback();
  };

  const frameHandle = requestFrame(() => {
    if (cancelled) {
      return;
    }

    if (postPaintWindow.requestIdleCallback) {
      idleHandle = postPaintWindow.requestIdleCallback(
        () => {
          runCallback();
        },
        { timeout: idleTimeoutMs },
      );
      return;
    }

    timeoutHandle = window.setTimeout(runCallback, fallbackDelayMs);
  });

  return () => {
    cancelled = true;
    cancelFrame(frameHandle);

    if (idleHandle !== null && postPaintWindow.cancelIdleCallback) {
      postPaintWindow.cancelIdleCallback(idleHandle);
    }

    if (timeoutHandle !== null) {
      window.clearTimeout(timeoutHandle);
    }
  };
}
